/**
 * IP18 Optical Matrix — two chapter pin-switches.
 * Desktop (>=1024): page-scroll scrubs intro, overflow pan, and a full-viewport strip.
 * Zone titles overlay the strip and stay put.
 * Mobile (<1024): stacked document flow, no pin. Desktop keeps intro scrub and strip.
 */
(function () {
  'use strict';

  var HEIGHT_EPS = 2;
  var LG = 1024;

  function headerSection() {
    var nav = document.querySelector('header-navigation');
    if (!nav) return null;
    return nav.closest('[id^="shopify-section-"]') || nav;
  }

  function headerH() {
    var header = headerSection();
    if (!header) return 0;
    var pos = window.getComputedStyle(header).position;
    if (pos !== 'sticky' && pos !== 'fixed') return 0;
    return header.getBoundingClientRect().height;
  }

  function bannerBarH() {
    var bar = document.querySelector('[data-ip18-banner-bar]');
    if (!bar) return 0;
    return bar.offsetHeight;
  }

  function pinChrome() {
    return Math.round(headerH() + bannerBarH());
  }

  function clamp(n, min, max) {
    return Math.min(max, Math.max(min, n));
  }

  function lerp(a, b, t) {
    return a + (b - a) * t;
  }

  function mapRange(p, a0, a1) {
    if (p <= a0) return 0;
    if (p >= a1) return 1;
    var span = a1 - a0;
    if (!span) return 1;
    return (p - a0) / span;
  }

  function easeInOut(t) {
    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
  }

  function applyLayer(el, opacity, ty) {
    if (!el) return;
    var op = opacity.toFixed(3);
    ty = Math.round(ty);
    var tr = 'translate3d(0,' + ty + 'px,0)';
    if (el._ip18op !== op) {
      el._ip18op = op;
      el.style.opacity = op;
      var hide = opacity < 0.05;
      el.style.pointerEvents = hide ? 'none' : 'auto';
      el.setAttribute('aria-hidden', hide ? 'true' : 'false');
    }
    if (el._ip18tr !== tr) {
      el._ip18tr = tr;
      el.style.transform = tr;
    }
  }

  class Ip18PinSwitch extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this._hasIntro = this.getAttribute('data-has-intro') === 'true';
      this._track = this.querySelector('[data-ip18-track]');
      this._sticky = this.querySelector('[data-ip18-sticky]');
      this._intro = this.querySelector('[data-ip18-intro]');
      this._eyebrow = this.querySelector('[data-ip18-intro-eyebrow]');
      this._heading = this.querySelector('[data-ip18-intro-heading]');
      this._body = this.querySelector('[data-ip18-intro-body]');
      this._shell = this.querySelector('[data-ip18-zone-shell]');
      this._head = this.querySelector('.ip18-optical__zone-head');
      this._stage = this.querySelector('[data-ip18-stage]');
      this._strip = this.querySelector('[data-ip18-strip]');
      this._zoneInner = this.querySelector('[data-ip18-zone-inner]');
      this._stack = this.querySelector('[data-ip18-stack]');
      this._panels = Array.prototype.slice.call(this.querySelectorAll('[data-ip18-panel]'));
      this._inners = Array.prototype.slice.call(this.querySelectorAll('[data-ip18-inner]'));
      this._root = this.closest('.ip18-optical') || this;
      this._phases = {
        introPx: 0,
        coverPx: 0,
        o1: 0,
        holdPx: 0,
        resetPx: 0,
        switchPx: 0,
        holdAfterPx: 0,
        o2: 0,
        o2Px: 0,
        restPx: 0
      };
      this._chrome = 0;
      this._pinH = 0;
      this._slideT = -1;
      this._reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
      this._onScroll = this._onScroll.bind(this);
      this._onReduce = this._onReduce.bind(this);
      this._onResize = this._onResize.bind(this);
      this._onWinResize = this._onWinResize.bind(this);

      if (!this._hasIntro) this._setShellSettled();

      this._reduce.addEventListener('change', this._onReduce);
      this._placePanels();
      this._syncFlowClass();
      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onWinResize, { passive: true });
      window.addEventListener('load', this._onWinResize);
      this._onWinResize();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onWinResize);
      window.removeEventListener('load', this._onWinResize);
      if (this._reduce) this._reduce.removeEventListener('change', this._onReduce);
    }

    _isMobile() {
      return window.innerWidth < LG;
    }

    _shouldPin() {
      if (!this._reduce || this._reduce.matches) return false;
      return window.innerWidth >= LG;
    }

    _isMobileFlow() {
      return window.innerWidth < LG;
    }

    _placePanels() {
      var introPin = this._shouldPin() && this._isMobile() && this._hasIntro && this._stack;
      if (this._zoneInner && this._sticky && this._stack) {
        if (introPin) {
          if (this._zoneInner.parentElement !== this._stack) {
            this._stack.insertBefore(this._zoneInner, this._stack.firstChild);
          }
        } else if (this._zoneInner.parentElement !== this._sticky) {
          this._sticky.appendChild(this._zoneInner);
        }
      }

      var panel2 = this._panels[1];
      if (panel2 && this._strip) {
        if (introPin) {
          if (panel2.parentElement !== this._stack) this._stack.appendChild(panel2);
        } else if (panel2.parentElement !== this._strip) {
          this._strip.appendChild(panel2);
        }
      }

      this.classList.toggle('is-intro-pin', !!(introPin && this._zoneInner && this._zoneInner.parentElement === this._stack));
      this.classList.toggle('is-pin-solo', !!(introPin && panel2 && panel2.parentElement === this._stack));
      this._slideT = -1;
      this._setInnerY(0, 0);
      this._setInnerY(1, 0);
      if (this._strip && (introPin || !this._shouldPin())) {
        this._strip.style.transform = 'none';
      }
      if (introPin) this._setShellSettled();
      else this._setStackOverlap(0);
    }

    _setStackOverlap(px) {
      if (!this._stack) return;
      var next = px ? -Math.round(px) + 'px' : '';
      if (this._stack.style.marginTop === next) return;
      this._stack.style.marginTop = next;
    }

    _syncFlowClass() {
      var mobile = this._isMobile();
      var reduced = this._reduce && this._reduce.matches;
      this.classList.toggle('is-reduced', reduced);
      this.classList.toggle('is-mobile-flow', this._isMobileFlow());
      if (this._isMobileFlow() && this._hasIntro) {
        applyLayer(this._intro, 1, 0);
        applyLayer(this._eyebrow, 1, 0);
        applyLayer(this._heading, 1, 0);
        applyLayer(this._body, 1, 0);
        this._setShellSettled();
        this._setStackOverlap(0);
      }
      if (reduced && !mobile) {
        if (this._track) this._track.style.height = '';
        if (this._hasIntro) applyLayer(this._intro, 0, 0);
        this._setShellSettled();
        this._setPanelsSlide(0);
        this._setInnerY(0, 0);
        this._setInnerY(1, 0);
        this._syncHeadPad();
      }
    }

    _syncHeadPad() {
      if (!this._shell) return;
      if (!this._shouldPin() || !this._head || this._isMobile()) {
        this._shell.style.setProperty('--ip18-head-overlay-pad', '0px');
        return;
      }
      var h = Math.round(this._head.offsetHeight);
      this._shell.style.setProperty('--ip18-head-overlay-pad', Math.max(0, h) + 'px');
    }

    _onReduce() {
      this._placePanels();
      this._syncFlowClass();
      this._onResize();
    }

    _onWinResize() {
      var w = window.innerWidth;
      var h = window.innerHeight;
      if (
        this._layoutW === w &&
        this._layoutH != null &&
        Math.abs(h - this._layoutH) < 80
      ) {
        this._syncHeadPad();
        return;
      }
      this._layoutW = w;
      this._layoutH = h;
      this._chromeReady = false;
      this._pinH = 0;
      this._onResize();
    }

    _onResize() {
      var self = this;
      if (this._resizeTick) return;
      this._resizeTick = true;
      requestAnimationFrame(function () {
        self._resizeTick = false;
        self._placePanels();
        self._syncFlowClass();
        self._measure();
        self._onScroll();
      });
    }

    _freezeChrome() {
      if (this._chromeReady) return this._chrome;
      var next = pinChrome();
      this._chromeReady = true;
      this._chrome = next;
      this._root.style.setProperty('--ip18-optical-sticky-top', next + 'px');
      return next;
    }

    _stickyH() {
      if (this._pinH > 1) return this._pinH;
      if (this._sticky && this._sticky.clientHeight > 1) return this._sticky.clientHeight;
      return Math.max(1, window.innerHeight - this._chrome);
    }

    _setShellSettled() {
      var shell = this._shell;
      if (!shell) return;
      shell.classList.add('is-risen');
      shell.style.opacity = '';
      shell.style.transform = '';
      shell.style.pointerEvents = '';
      shell._ip18op = null;
      shell._ip18tr = null;
    }

    _setShellRise(rise, pinH) {
      var shell = this._shell;
      if (!shell) return;
      if (rise >= 0.995) {
        this._setShellSettled();
        return;
      }
      if (shell.classList.contains('is-risen')) {
        shell.classList.remove('is-risen');
      }
      applyLayer(shell, rise, lerp(pinH, 0, rise));
    }

    _measure() {
      if (!this._track) return;
      if (!this._shouldPin()) {
        this._track.style.height = '';
        this._slideT = -1;
        this._setInnerY(0, 0);
        this._setInnerY(1, 0);
        if (this._strip) this._strip.style.transform = 'none';
        this._setStackOverlap(0);
        this._syncHeadPad();
        return;
      }

      var chrome = this._freezeChrome();
      var pinH =
        this._sticky && this._sticky.clientHeight > 1
          ? this._sticky.clientHeight
          : Math.max(1, Math.round(window.innerHeight - chrome));
      if (this._pinH > 1 && Math.abs(pinH - this._pinH) < HEIGHT_EPS) {
        pinH = this._pinH;
      } else {
        this._pinH = pinH;
      }

      this._syncHeadPad();

      var mobileIntro = this._isMobile() && this._hasIntro;
      if (mobileIntro) {
        this._phases = {
          introPx: Math.round(pinH * 0.9),
          coverPx: 0,
          o1: 0,
          holdPx: 0,
          resetPx: 0,
          switchPx: 0,
          holdAfterPx: 0,
          o2: 0,
          o2Px: 0,
          restPx: 0
        };
        var extraM = this._phases.introPx;
        var nextHM = pinH + extraM;
        var prevHM = parseFloat(this._track.style.height) || 0;
        if (!prevHM || Math.abs(nextHM - prevHM) >= HEIGHT_EPS) {
          this._track.style.height = nextHM + 'px';
        }
        this._setStackOverlap(pinH);
        return;
      }

      var stageH = this._stage && this._stage.clientHeight > 1 ? this._stage.clientHeight : pinH;
      var pinInners = this._strip
        ? Array.prototype.slice.call(this._strip.querySelectorAll('[data-ip18-inner]'))
        : this._inners;

      var overflows = pinInners.map(function (inner) {
        if (!inner) return 0;
        var panel = inner.parentElement;
        var avail = stageH;
        if (panel) {
          var cs = window.getComputedStyle(panel);
          avail =
            panel.clientHeight -
            (parseFloat(cs.paddingTop) || 0) -
            (parseFloat(cs.paddingBottom) || 0);
        }
        if (avail < 1) avail = stageH;
        return Math.max(0, Math.ceil(inner.scrollHeight - avail));
      });

      pinInners.forEach(function (inner, i) {
        if (!inner) return;
        var on = (overflows[i] || 0) > 0;
        if (inner.classList.contains('is-overflowing') !== on) {
          inner.classList.toggle('is-overflowing', on);
        }
      });

      var o1 = overflows[0] || 0;
      var o2 = overflows[1] || 0;
      var hasSwitch = pinInners.length > 1;

      this._phases = {
        introPx: this._hasIntro ? Math.round(pinH * 2) : 0,
        coverPx: 0,
        o1: o1,
        holdPx: Math.round(pinH * 0.4),
        resetPx: o1 > 0 ? Math.round(pinH * 0.45) : 0,
        switchPx: hasSwitch ? Math.round(pinH * 1.15) : 0,
        holdAfterPx: hasSwitch ? Math.round(pinH * 0.65) : 0,
        o2: o2,
        o2Px: o2 > 0 ? Math.round(o2 * 1.25) : 0,
        restPx: Math.round(pinH * (o2 > 0 ? 1.2 : 0.7))
      };

      var extra =
        this._phases.introPx +
        this._phases.coverPx +
        this._phases.o1 +
        this._phases.holdPx +
        this._phases.resetPx +
        this._phases.switchPx +
        this._phases.holdAfterPx +
        this._phases.o2Px +
        this._phases.restPx;
      var nextH = pinH + extra;
      var prevH = parseFloat(this._track.style.height) || 0;
      if (!prevH || Math.abs(nextH - prevH) >= HEIGHT_EPS) {
        this._track.style.height = nextH + 'px';
      }
    }

    _setInnerY(index, y) {
      var inner = this._inners[index];
      if (!inner) return;
      y = Math.round(y);
      if (inner._ip18y === y) return;
      inner._ip18y = y;
      inner.style.transform = y ? 'translate3d(0,' + y + 'px,0)' : '';
    }

    _setPanelsSlide(t) {
      if (this._slideT === t) return;
      this._slideT = t;

      if (this._strip) {
        if (!this._shouldPin() || this._isMobile() || this.classList.contains('is-intro-pin') || this.classList.contains('is-pin-solo')) {
          this._strip.style.transform = 'none';
        } else {
          this._strip.style.transform = 'translate3d(' + -t * 50 + '%,0,0)';
        }
      }

      this._panels.forEach(function (panel, i) {
        var stacked = this._stack && panel.parentElement === this._stack;
        var on = stacked || (i === 0 ? t < 0.5 : i === 1 ? t >= 0.5 : false);
        if (this._panels.length < 2) on = i === 0;
        panel.classList.toggle('is-active', on);
        panel.removeAttribute('aria-hidden');
        panel.style.pointerEvents = '';
        panel.style.opacity = '';
      }, this);
    }

    _scrubIntro(scrolled, pinH, introPx) {
      var introT = mapRange(scrolled, 0, Math.max(1, introPx));
      var mobile = this._isMobile();
      var smallFade = easeInOut(mapRange(introT, 0.04, mobile ? 0.32 : 0.28));
      var headingT = easeInOut(mapRange(introT, mobile ? 0.28 : 0.38, mobile ? 0.92 : 0.78));
      var rise = easeInOut(mapRange(introT, 0.46, 0.92));
      var headingY = lerp(0, -pinH * (325 / 1080), headingT);

      applyLayer(this._eyebrow, 1 - smallFade, 0);
      applyLayer(this._body, 1 - smallFade, 0);
      applyLayer(this._heading, 1 - headingT, headingY);
      if (mobile) {
        applyLayer(this._intro, 1 - headingT, 0);
        this._setShellSettled();
      } else {
        applyLayer(this._intro, rise >= 0.99 ? 0 : 1, 0);
        this._setShellRise(rise, pinH);
      }
      var riseKey = rise.toFixed(3);
      if (this.dataset.rise !== riseKey) this.dataset.rise = riseKey;
      return rise;
    }

    _onScroll() {
      if (!this._track) return;
      if (this._reduce.matches) return;
      if (!this._shouldPin()) return;

      var chrome = this._chromeReady ? this._chrome : this._freezeChrome();
      var pinH = this._stickyH();
      var extra = Math.max(1, this._track.offsetHeight - pinH);
      var scrolled = clamp(chrome - this._track.getBoundingClientRect().top, 0, extra);
      var prog = (scrolled / extra).toFixed(3);
      if (this.dataset.progress !== prog) this.dataset.progress = prog;

      var introPx = this._phases.introPx;
      var o1 = this._phases.o1;
      var holdPx = this._phases.holdPx;
      var resetPx = this._phases.resetPx;
      var switchPx = this._phases.switchPx;
      var holdAfterPx = this._phases.holdAfterPx;
      var o2 = this._phases.o2;
      var o2Px = this._phases.o2Px;
      var y = scrolled;

      if (this._hasIntro) {
        this._scrubIntro(scrolled, pinH, introPx);
        y = scrolled - introPx;
        if (this._isMobile()) {
          this._setInnerY(0, 0);
          this._setInnerY(1, 0);
          this._setPanelsSlide(0);
          return;
        }
      } else {
        this._setShellSettled();
        y = scrolled;
      }

      if (y <= 0) {
        this._setInnerY(0, 0);
        this._setInnerY(1, 0);
        this._setPanelsSlide(0);
        return;
      }

      var read1 = clamp(y, 0, o1);
      this._setInnerY(0, -read1);
      if (y <= o1) {
        this._setInnerY(1, 0);
        this._setPanelsSlide(0);
        return;
      }

      y -= o1;
      if (y <= holdPx) {
        this._setInnerY(0, -o1);
        this._setInnerY(1, 0);
        this._setPanelsSlide(0);
        return;
      }

      y -= holdPx;
      if (y <= resetPx) {
        var rewind = easeInOut(mapRange(y, 0, Math.max(1, resetPx)));
        this._setInnerY(0, lerp(-o1, 0, rewind));
        this._setInnerY(1, 0);
        this._setPanelsSlide(0);
        return;
      }

      this._setInnerY(0, 0);
      y -= resetPx;
      var slideT = switchPx > 0 ? clamp(y / switchPx, 0, 1) : 1;
      this._setPanelsSlide(easeInOut(slideT));

      if (y <= switchPx) {
        this._setInnerY(1, 0);
        return;
      }

      y -= switchPx;
      this._setPanelsSlide(1);
      if (y <= holdAfterPx) {
        this._setInnerY(1, 0);
        return;
      }

      y -= holdAfterPx;
      if (o2Px > 0) {
        this._setInnerY(1, -Math.round(clamp(y / o2Px, 0, 1) * o2));
      } else {
        this._setInnerY(1, 0);
      }
    }
  }

  if (!customElements.get('ip18-pin-switch')) {
    customElements.define('ip18-pin-switch', Ip18PinSwitch);
  }
})();
